'use client';

import React, { useEffect, useState, useRef } from 'react';
import SwiperCore from 'swiper';
import { Pagination, Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import { HeroBanner } from '@/types/homepage/homepageTypes';
import Head from 'next/head';
import Button from '../ui/button';
import { usePostHogEvents } from '@/hooks/usePostHogEvents';
SwiperCore.use([Pagination, Autoplay, Navigation]);

const HeroSlider = ({ blogData }: { blogData: HeroBanner[] }) => {
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  const swiperRef = useRef<any>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const { sendButtonClickEvent } = usePostHogEvents();
  const toggleAutoplay = () => {
    if (swiperRef.current) {
      const swiper = swiperRef?.current?.swiper;

      if (swiper?.autoplay?.running) {
        swiper?.autoplay?.stop();
        setAutoplayPaused(true);
      } else {
        swiper?.autoplay?.start();
        setAutoplayPaused(false);
      }
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (imageRef?.current) {
        imageRef?.current?.remove();
      }

      const customImageEl = document.createElement('img');
      customImageEl.alt = autoplayPaused
        ? 'Play button for slideshow'
        : 'Pause button for slideshow';
      customImageEl.title = autoplayPaused
        ? 'Play Slideshow'
        : 'Pause Slideshow';
      customImageEl.src = autoplayPaused
        ? '/icons/play1.svg'
        : '/icons/pause1.svg';
      customImageEl.className = 'w-[13px] h-[13px] cursor-pointer invert';

      customImageEl.addEventListener('click', () => toggleAutoplay());

      const paginationContainer = document.querySelector(
        '.swiper_container1 .swiper-pagination'
      );

      if (paginationContainer) {
        paginationContainer.appendChild(customImageEl);
        imageRef.current = customImageEl;
      }

      return () => {
        if (imageRef.current) {
          imageRef.current.remove();
        }
      };
    }
  }, [autoplayPaused]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const swiperInstance = swiperRef.current?.swiper;
      if (!swiperInstance) return;

      // Handler for interactions that should pause autoplay
      const handleInteraction = () => {
        if (swiperInstance.autoplay.running) {
          swiperInstance.autoplay.stop();
          setAutoplayPaused(true);
        }
      };

      // Attach event listeners to navigation buttons
      const navigationButtons = document.querySelectorAll(
        '.swiper-button-next, .swiper-button-prev'
      );
      navigationButtons.forEach((button) =>
        button.addEventListener('click', handleInteraction)
      );

      // Attach event listeners to pagination bullets
      const paginationBullets = document.querySelectorAll(
        '.swiper-pagination-bullet'
      );
      paginationBullets.forEach((bullet) =>
        bullet.addEventListener('click', handleInteraction)
      );

      // Listen for the end of a drag (touchEnd event)
      swiperInstance.on('touchEnd', handleInteraction);

      return () => {
        // Clean up the event listeners
        navigationButtons.forEach((button) =>
          button.removeEventListener('click', handleInteraction)
        );
        paginationBullets.forEach((bullet) =>
          bullet.removeEventListener('click', handleInteraction)
        );
        swiperInstance.off('touchEnd', handleInteraction);

        if (imageRef.current) {
          imageRef.current.remove();
        }
      };
    }
  }, []);

  const sizes = '(max-width: 639px) 100vw, (max-width: 1024px) 100vw, 1200px';

  return (
    <>
      <Head>
        {/* Preload the first image for LCP improvement */}
        {blogData && blogData[0]?.media && (
          <link
            rel="preload"
            as="image"
            imageSizes={sizes}
            href={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${blogData[0]?.media}`}
            imageSrcSet={`
            ${process.env.NEXT_PUBLIC_BASE_URL}/assets/${blogData[0]?.mobile_media} 640w,
            ${process.env.NEXT_PUBLIC_BASE_URL}/assets/${blogData[0]?.media} 1200w
          `}
          />
        )}

        <link rel="preload" href="../blogs/blog_carousel.css" as="style" />
        <link rel="preload" href="../hero/hero_carousel.css" as="style" />
      </Head>

      <div className="max-w-desktop md:h-[750px] h-[600px] bg-black md:rounded-[10px] overflow-hidden md:mx-auto ">
        <Swiper
          ref={swiperRef}
          effect="slide"
          preventClicks={false}
          preventClicksPropagation={false}
          noSwipingSelector="a"
          pagination={{
            clickable: true,
          }}
          loop={true}
          navigation={blogData?.length > 1}
          autoplay={{
            delay: 8000,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
            waitForTransition: true, // wait until page is interactive
          }}
          className={cn(
            'swiper_container1  bg-black overflow-hidden',
            autoplayPaused ? 'paused' : ''
          )}
        >
          {blogData?.map((props, idx: number) => (
            <SwiperSlide key={idx}>
              <div className=" w-full bg-black h-full relative">
                <div className="w-full h-full relative">
                  {props?.mobile_media ? (
                    <>
                      <Image
                        src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${props.mobile_media}`}
                        alt={props?.title}
                        title={props?.title}
                        fill
                        priority={idx === 0}
                        loading={idx === 0 ? 'eager' : 'lazy'}
                        sizes="(max-width: 639px) 100vw, 0px"
                        quality={80}
                        className="object-cover select-none block sm:hidden"
                      />
                      <Image
                        src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${props.media}`}
                        alt={props?.title}
                        title={props?.title}
                        fill
                        priority={idx === 0}
                        loading={idx === 0 ? 'eager' : 'lazy'}
                        sizes="(max-width: 639px) 0px, (max-width: 1200px) 100vw, 1200px"
                        quality={80}
                        className="object-cover select-none hidden sm:block"
                      />
                    </>
                  ) : (
                    <Image
                      src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${props?.media}`}
                      alt={props?.title}
                      title={props?.title}
                      fill
                      priority={idx === 0}
                      loading={idx === 0 ? 'eager' : 'lazy'}
                      sizes={sizes}
                      quality={80}
                      className="object-cover select-none"
                    />
                  )}
                </div>

                <div className="absolute inset-0 flex items-center px-6 pt-14 md:pt-0 ">
                  <div className="md:w-[60%] md:max-lg:w-[72%] w-full md:px-24 md:max-lg:px-14 px-0 justify-start md:justify-center h-full text-white md:items-start items-center md:text-left text-center flex flex-col gap-[12px] md:gap-[8px]">
                    <h1 className="font-bold sselect-none md:text-[40px] text-[24px] leading-[32px] md:leading-tight">
                      {props?.title}
                    </h1>
                    <p className="md:text-[18px] text-[16px] select-none font-proxima_font">
                      {props?.description}
                    </p>

                    {props?.has_cta && (
                      <Button
                        onClick={() => {
                          sendButtonClickEvent(
                            props?.cta_button_text,
                            props?.title
                          );
                        }}
                        label={(props?.cta_button_text + '>') as string}
                        to={props?.cta_button_url}
                        color="white"
                        className="text-[14px] font-semibold mt-4 absolute md:relative bottom-20 md:bottom-0 font-proxima_font testing"
                      />
                    )}
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </>
  );
};

export default HeroSlider;
